import { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, KeyRound, ExternalLink, Play, Square, RotateCcw, AlignLeft, Braces, ListChecks } from 'lucide-react';
import { Card, Alert, Badge, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi, useStableApi } from '@/lib/api';
import Can from '@rbac/Can';
import UsageCard from '@/components/apiUsage/UsageCard';
import {
  buildRunBodyExample, outputShapeFor, prettyJson, buildRunCurl,
  buildRunJsFetch, buildRunPythonRequests,
} from '@/lib/workflowApiExamples';
import InputSchemaForm, { namedFields } from '@/components/workflow/InputSchemaForm';
import { readInput, writeInput, valuesFromInput, applyValues } from '@/lib/runPayload';

const CodeEditor = lazy(() => import('@/components/CodeEditor'));

/**
 * The workflow's API tab — how to call this workflow over the public REST API
 * (/api/v1), which tokens may call it (multiple, workflow-bound), and how much each
 * key has been used (rollups over app_api_audit). Read-only documentation + token
 * management; issuing needs core:manage_tokens like the API Tokens screen.
 */

const EXPIRY_OPTIONS = [
  { value: '90', label: '90 days (recommended)' },
  { value: '30', label: '30 days' },
  { value: '365', label: '1 year' },
  { value: 'never', label: 'No expiry (not recommended)' },
];

const codeBlock = 'bg-[var(--app-surface)] rounded p-2.5 text-xs font-mono overflow-x-auto whitespace-pre border border-[var(--app-border)]/60';

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — the text is still selectable */ }
  };
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={copy}
        title="Copy to clipboard"
        className="absolute top-2 right-2 p-1.5 rounded-md text-secondary-500 hover:text-[var(--app-text)] hover:bg-[var(--app-border)]/50 transition-colors bg-[var(--app-surface)]/80 shadow-xs"
      >
        {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
      </button>
      <pre className={codeBlock}>{code}</pre>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <Card className="p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-sm">{title}</h3>
        {action}
      </div>
      {children}
    </Card>
  );
}

const lastUsedFor = (t, u) => {
  const at = (u && u.last_used_at) || t.last_used_at;
  return at ? new Date(at).toLocaleString() : 'never';
};

function statusBadge(row) {
  if (row.revoked_at) return <Badge variant="error" size="sm">revoked</Badge>;
  if (row.expires_at && new Date(row.expires_at) < new Date()) return <Badge variant="warning" size="sm">expired</Badge>;
  return <Badge variant="success" size="sm">active</Badge>;
}

/* --------------------------------------------------------------- hero endpoint card -- */

function EndpointHeroCard({ form }) {
  const [copied, setCopied] = useState(false);
  const base = `${window.location.origin}/api/v1`;
  const path = form.key || form.id;
  const runUrl = `${base}/workflows/${path}/run`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(runUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <Card className="p-3 bg-[var(--app-surface)] border-[var(--app-border)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          <span className="px-2 py-0.5 rounded-md font-mono text-xs font-bold uppercase bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
            POST
          </span>
          <code className="font-mono text-xs sm:text-sm font-semibold text-[var(--app-text)] break-all bg-[var(--app-surface)]/80 px-2.5 py-1 rounded-md border border-[var(--app-border)]">
            {runUrl}
          </code>
          <button
            type="button"
            onClick={copyUrl}
            className="p-1.5 rounded-md text-secondary-500 hover:text-[var(--app-text)] hover:bg-[var(--app-border)]/50 transition-colors"
            title="Copy endpoint URL"
          >
            {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
          </button>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-secondary-500">Required:</span>
          <Badge variant="outline" size="sm">workflows:run</Badge>
          <Badge variant="outline" size="sm">workflows:view</Badge>
        </div>
      </div>
      <p className="text-xs text-secondary-500 mt-2">
        Triggers an asynchronous workflow execution. Returns <code>202 Accepted</code> with a <code>run_id</code>.
        Add <code>?wait=N</code> (1–60s) to long-poll for up to N seconds before falling back to manual polling.
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------ tokens section -- */

function expiresAtFromDays(days) {
  return days == null ? null : new Date(Date.now() + days * 24 * 3600 * 1000).toISOString().slice(0, 19).replace('T', ' ');
}

async function issueWorkflowToken(fetchApi, { workflowId, name, days }) {
  const res = await fetchApi('crud', {
    model: 'ApiToken', action: 'issue',
    data: { name, expires_at: expiresAtFromDays(days), workflow_id: workflowId, scopes: [] },
  });
  return (res.data && res.data.token) || null;
}

/** The Try-it token Execute itself manages. The prefix namespaces it, so real
 *  integration tokens — any other name in the table — are never touched. */
const TRY_IT_PREFIX = 'Try it — ';

/** This workflow's active Try-it rows (Execute-created only). */
async function findTryItTokens(fetchApi, workflowId) {
  const res = await fetchApi('crud', { model: 'ApiToken', action: 'list', where: { workflow_id: workflowId }, limit: 100 });
  const rows = (Array.isArray(res) ? res : (res && res.data)) || [];
  return rows.filter((t) => t && !t.revoked_at && typeof t.name === 'string' && t.name.startsWith(TRY_IT_PREFIX));
}

/** Same revoke action the API Tokens detail screen uses. */
async function revokeWorkflowToken(fetchApi, id) {
  await fetchApi('crud', { model: 'ApiToken', action: 'revoke', where: { id }, data: { id } });
}

function IssueWorkflowToken({ workflowId, onIssued }) {
  const { fetchApi } = useAppApi();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('90');
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const raw = await issueWorkflowToken(fetchApi, {
        workflowId, name, days: expiry === 'never' ? null : Number(expiry),
      });
      if (raw) {
        setRevealed(raw);
        onIssued(raw);
      }
      setOpen(false);
      setName('');
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  if (revealed) {
    return (
      <Alert variant="warning" title="Workflow token — copy it now, it will not be shown again">
        <code className="block break-all text-xs bg-[var(--app-surface)] rounded p-2 my-2">{revealed}</code>
        <p className="text-xs text-secondary-500 mb-2">
          Bound to this workflow with <code>workflows:view</code> + <code>workflows:run</code>. The
          token&apos;s detail screen (API Tokens page) shows its usage from now on.
        </p>
        <Button size="sm" variant="secondary" onClick={() => setRevealed(null)}>I saved it</Button>
      </Alert>
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        <KeyRound size={13} className="inline mr-1" />Issue workflow token
      </Button>
    );
  }
  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Issue token for this workflow</h3>
        <button className="text-sm text-secondary-500 hover:text-[var(--app-text)]" onClick={() => setOpen(false)}>Close</button>
      </div>
      <p className="text-xs text-secondary-500">
        Automatically bound to this workflow and granted <code>workflows:view</code> + <code>workflows:run</code>.
        The raw value is shown once, at issue time.
      </p>
      {err && <Alert variant="error" title="Could not issue token">{err}</Alert>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Name (what integration uses it)" value={name} onChange={(e) => setName(e.target.value)} />
        <Select label="Expiry" options={EXPIRY_OPTIONS} value={expiry} onChange={setExpiry} />
      </div>
      <div className="text-right">
        <Button onClick={submit} disabled={busy || !name.trim()}>{busy ? 'Issuing…' : 'Issue token'}</Button>
      </div>
    </Card>
  );
}

function TokenPrefixCopy({ prefix }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prefix);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignored */ }
  };
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs text-secondary-500">
      {prefix}…
      <button type="button" onClick={copy} title="Copy token prefix" className="hover:text-[var(--app-text)] p-0.5">
        {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
      </button>
    </span>
  );
}

function TokensSection({ workflowId, onIssued, reloadKey = 0 }) {
  const { fetchApi } = useStableApi();
  const nav = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchApi('crud', { model: 'ApiToken', action: 'list', where: { workflow_id: workflowId }, limit: 100 }),
      fetchApi('apiUsageSummary', { workflow_id: workflowId }).catch(() => null),
    ])
      .then(([tokensRes, usageRes]) => {
        setTokens((Array.isArray(tokensRes) ? tokensRes : tokensRes?.data) || []);
        setUsage(usageRes);
      })
      .finally(() => setLoading(false));
  }, [workflowId]);

  // reloadKey bumps when Execute reuses/refreshes the Try-it token, so the refreshed
  // row (new prefix, updated last-used) shows without a manual reload.
  useEffect(() => { load(); }, [load, reloadKey]);

  const perToken = new Map((usage?.per_token || []).map((t) => [t.token_id, t]));
  const managed = tokens.some((t) => !t.revoked_at);

  return (
    <Section
      title="API Tokens for this Workflow"
      action={
        <Can module="core" action="manage_tokens">
          <IssueWorkflowToken
            workflowId={workflowId}
            onIssued={(raw) => { load(); onIssued && onIssued(raw); }}
          />
        </Can>
      }
    >
      <p className="text-xs text-secondary-500">
        Each key is a separate credential for one integration — issue one per caller so its
        usage shows up separately below. Company-wide tokens can also call this workflow if their scopes allow.
      </p>
      {!loading && !tokens.length && (
        <p className="text-xs text-secondary-500 py-2">No workflow-bound tokens issued yet.</p>
      )}
      {tokens.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-secondary-500 border-b border-[var(--app-border)]">
                <th className="py-2 pr-3 font-semibold">Token Name</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 pr-3 font-semibold">Requests (30d)</th>
                <th className="py-2 pr-3 font-semibold">Errors</th>
                <th className="py-2 pr-3 font-semibold">Last Used</th>
                <th className="py-2 font-semibold">Expires</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => {
                const u = perToken.get(t.id);
                return (
                  <tr key={t.id} className="border-b border-[var(--app-border)]/50 hover:bg-[var(--app-surface)]/50 transition-colors">
                    <td className="py-2 pr-3">
                      <button onClick={() => nav(`/api-tokens/${t.id}`)} className="text-primary-600 hover:underline font-medium text-left">
                        {t.name || '(unnamed)'}
                      </button>
                      <span className="ml-2"><TokenPrefixCopy prefix={t.token_prefix} /></span>
                    </td>
                    <td className="py-2 pr-3">{statusBadge(t)}</td>
                    <td className="py-2 pr-3 font-mono">{u ? u.requests : '—'}</td>
                    <td className="py-2 pr-3 font-mono">{u ? u.errors : '—'}</td>
                    <td className="py-2 pr-3 text-secondary-500">{lastUsedFor(t, u)}</td>
                    <td className="py-2 text-secondary-500">{t.expires_at ? new Date(t.expires_at).toLocaleDateString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {managed && (
        <div className="pt-1">
          <a href="/api-tokens" className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline">
            Manage all company tokens <ExternalLink size={11} />
          </a>
        </div>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------- usage section -- */

function UsageSection({ workflowId }) {
  return <UsageCard workflowId={workflowId} />;
}

/* ------------------------------------------------------------------ execute section -- */

/** Statuses that stop the poll: the terminal three plus every PARKED status. A parked run
 *  will not move on its own within the panel's window — it is waiting on a human, on the
 *  calling application, or on a timer — so polling on would spin for the full five minutes
 *  and never render anything. Must stay in step with the server's POLL_SETTLED_STATUSES
 *  (routes/publicApiRouter.js). */
const TERMINAL_RUN_STATUSES = ['done', 'error', 'cancelled', 'waiting_for_client', 'waiting_for_timer'];
const RUN_STATUS_VARIANTS = {
  done: 'success',
  error: 'error',
  cancelled: 'warning',
  waiting_for_client: 'warning',
  waiting_for_timer: 'warning',
};
/** Why a parked run is parked — without this the badge alone reads like a failure. */
const RUN_STATUS_HINTS = {
  waiting_for_client: 'waiting for your application to POST the client-tool results',
  waiting_for_timer: 'parked on a Wait node — it continues by itself when the timer fires',
};
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 5 * 60 * 1000;

function runStatusBadge(status) {
  const variant = RUN_STATUS_VARIANTS[status];
  return variant ? <Badge variant={variant} size="sm">{status}</Badge> : <Badge size="sm">{status}</Badge>;
}

function buildRequestBody(bodyText, idempotencyKey) {
  const parsed = JSON.parse(bodyText);
  if (idempotencyKey && idempotencyKey.trim()) parsed.idempotency_key = idempotencyKey.trim();
  return prettyJson(parsed);
}

function curlBodyFor(bodyText, idempotencyKey) {
  try {
    return buildRequestBody(bodyText, idempotencyKey);
  } catch {
    return bodyText;
  }
}

/** After the POST resolves: a terminal run's steps need one GET (the POST body carries only
 *  the run), a still-running one enters the poll loop, and a 202 hands off on run_id. */
async function handleRunResponse(data, { setRun, startPolling, loadOnce }) {
  if (data && data.run) {
    if (TERMINAL_RUN_STATUSES.includes(data.run.status)) return loadOnce(data.run.id);
    setRun(data.run);
    return;
  }
  if (data && data.run_id) startPolling(data.run_id);
}

const FAIL_HINTS = {
  401: 'token rejected (revoked, expired, or not a tnt_ token)',
  403: 'token lacks the workflows:run scope',
  404: 'workflow not found for this token',
  429: 'rate limit or in-flight cap hit',
};

function failHintFor(result) {
  if (result.status === 400 && result.data && result.data.error) return result.data.error;
  if (FAIL_HINTS[result.status]) return FAIL_HINTS[result.status];
  if (result.status >= 500) return 'server error, check the server logs';
  return null;
}

function RunStatusRow({ run }) {
  if (!run) return null;
  return (
    <div className="flex items-center gap-2 text-xs flex-wrap">
      {runStatusBadge(run.status)}
      {RUN_STATUS_HINTS[run.status] && (
        <span className="text-secondary-500">{RUN_STATUS_HINTS[run.status]}</span>
      )}
    </div>
  );
}

function TerminalOutput({ run }) {
  if (!run || !TERMINAL_RUN_STATUSES.includes(run.status) || run.output == null) return null;
  return <CodeBlock code={prettyJson({ output: run.output })} />;
}

/** Step-timeline rail colour — same keying as the Runs tab (RunsPanel.jsx), so a step reads
 *  identically in both places. Anything not ok/error is treated as in-flight. */
const CONSOLE_STEP_RAIL = { ok: '#16a34a', error: '#dc2626' };
const consoleStepRailColor = (status) => CONSOLE_STEP_RAIL[status] || '#d97706';

/** The execution plan as it ran — GET /runs/:id's `steps` (seq, node, type, status, output,
 *  error, branch, duration). Collapsed by default: the console's primary answer is the run
 *  result, this is the how-it-got-there, and payloads can still be large. */
function StepsTimeline({ steps }) {
  if (!steps || !steps.length) return null;
  return (
    <details className="pt-2 border-t border-[var(--app-border)]/60">
      <summary className="text-xs font-medium text-secondary-500 cursor-pointer">
        Execution plan — {steps.length} step{steps.length === 1 ? '' : 's'}
      </summary>
      <ol className="space-y-2 pt-2">
        {steps.map((s) => (
          <li key={`${s.seq}-${s.node_id}`} className="border-l-2 pl-3" style={{ borderColor: consoleStepRailColor(s.status) }}>
            <div className="flex justify-between gap-2 text-xs font-mono">
              <span className="min-w-0 break-all">
                {s.seq}. {s.node_id} <span className="text-secondary-500">({s.node_type})</span>
              </span>
              <span className="shrink-0 font-sans text-secondary-500">
                {s.status}{s.branch ? ` → ${s.branch}` : ''}{s.duration_ms != null ? ` · ${s.duration_ms}ms` : ''}
              </span>
            </div>
            {s.error && <div className="text-xs text-red-700">{s.error}</div>}
            {s.output != null && (
              <details>
                <summary className="text-xs text-secondary-500 cursor-pointer">output</summary>
                <CodeBlock code={prettyJson(s.output)} />
              </details>
            )}
          </li>
        ))}
      </ol>
    </details>
  );
}

function ResultView({ result, polling, run, steps }) {
  if (!result) return null;
  let statusVariant = 'success';
  if (result.status >= 500) statusVariant = 'error';
  else if (result.status >= 400) statusVariant = 'warning';
  const failHint = failHintFor(result);

  return (
    <div className="space-y-2 pt-2 border-t border-[var(--app-border)]/60">
      <div className="flex items-center gap-2 text-xs flex-wrap justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={statusVariant} size="sm">HTTP {result.status}</Badge>
          <span className="text-secondary-500 font-mono">{result.ms} ms</span>
          {failHint && result.status >= 400 && <span className="text-secondary-500">— {failHint}</span>}
        </div>
        {result.status === 202 && polling && (
          <div className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/30 px-2 py-0.5 rounded-md border border-primary-500/20">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
            </span>
            <span>polling run #{result.data && result.data.run_id}…</span>
          </div>
        )}
      </div>
      <RunStatusRow run={run} />
      {result.data != null && (
        <CodeBlock code={typeof result.data === 'string' ? result.data : prettyJson(result.data)} />
      )}
      <TerminalOutput run={run} />
      <StepsTimeline steps={steps} />
    </div>
  );
}

function useRunPolling({ base, token }) {
  const [polling, setPolling] = useState(false);
  const [run, setRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const timer = useRef(null);
  const active = useRef(false);

  const stop = useCallback(() => {
    active.current = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPolling(false);
  }, []);
  useEffect(() => () => stop(), [stop]);

  const start = useCallback((runId) => {
    active.current = true;
    setPolling(true);
    setSteps([]);
    const deadline = Date.now() + POLL_MAX_MS;
    const tick = async () => {
      if (!active.current) return;
      try {
        const res = await fetch(`${base}/runs/${runId}`, { headers: { Authorization: `Bearer ${token.trim()}` } });
        const data = await res.json().catch(() => null);
        if (data && data.run) {
          setRun(data.run);
          if (data.steps) setSteps(data.steps);
          if (TERMINAL_RUN_STATUSES.includes(data.run.status)) { stop(); return; }
        }
      } catch { /* transient — poll until the deadline */ }
      if (!active.current || Date.now() > deadline) { stop(); return; }
      timer.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    timer.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, [base, stop, token]);

  // An inline `?wait=30` run comes back terminal with no steps — the POST body carries
  // only the run. One GET fills the timeline so the long-poll path isn't the only one
  // that shows the execution plan.
  const loadOnce = useCallback(async (runId) => {
    try {
      const res = await fetch(`${base}/runs/${runId}`, { headers: { Authorization: `Bearer ${token.trim()}` } });
      const data = await res.json().catch(() => null);
      if (data && data.run) {
        setRun(data.run);
        if (data.steps) setSteps(data.steps);
      }
    } catch { /* best effort — the response body already shows the outcome */ }
  }, [base, token]);

  return { polling, run, steps, start, stop, setRun, loadOnce };
}

function TokenCaptureRow({ token, issuedToken, onToken, onForget }) {
  const usingIssued = Boolean(issuedToken) && token === issuedToken;
  return (
    <div className="space-y-1">
      <Input
        type="password"
        label="API Token (optional)"
        value={token}
        onChange={(e) => onToken(e.target.value)}
        placeholder="Leave blank to reuse this workflow's Try-it token, or paste a tnt_ token"
      />
      {issuedToken && !usingIssued && (
        <button type="button" onClick={() => onToken(issuedToken)} className="text-xs text-primary-600 hover:underline">
          Use the token issued just now
        </button>
      )}
      {usingIssued && (
        <p className="flex items-center gap-2 text-xs text-secondary-500">
          <Badge variant="success" size="sm">captured</Badge>
          using in-memory session token
          <button type="button" onClick={onForget} className="text-primary-600 hover:underline">
            forget it
          </button>
        </p>
      )}
    </div>
  );
}

function TryItTokenBlock({ tryIt, issuedToken, onForget }) {
  return (
    <>
      <TokenCaptureRow
        token={tryIt.token}
        issuedToken={issuedToken}
        onToken={tryIt.setToken}
        onForget={onForget}
      />
      {tryIt.autoIssued && (
        <p className="flex items-center gap-2 text-xs text-secondary-500">
          <Badge variant="success" size="sm">Try-it token</Badge>
          using this workflow&apos;s own Try-it token (listed below) — its 24h value was
          refreshed for this session and lives in memory only
        </p>
      )}
    </>
  );
}

function RunOptionsRow({ useWait, onUseWait, idempotencyKey, onIdempotencyKey }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
      <div className="flex items-center h-10 px-3 rounded-md border border-[var(--app-border)] bg-[var(--app-surface)]">
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
          <input
            type="checkbox"
            className="rounded border-[var(--app-border)] text-primary-600 focus:ring-primary-500"
            checked={useWait}
            onChange={(e) => onUseWait(e.target.checked)}
          />
          <span className="font-medium text-[var(--app-text)]">Long-poll up to 30s (?wait=30)</span>
        </label>
      </div>
      <div>
        <Input
          label="Idempotency key (optional)"
          value={idempotencyKey}
          onChange={(e) => onIdempotencyKey(e.target.value)}
          placeholder="e.g. key-123 — retries reuse original run"
        />
      </div>
    </div>
  );
}

function useTryItToken({ fetchApi, form, onIssued }) {
  const [token, setToken] = useState('');
  const [autoIssued, setAutoIssued] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState('');

  const ensureToken = async () => {
    if (token.trim()) return token.trim();
    setIssuing(true);
    setIssueError('');
    try {
      // Reuse the Try-it row this workflow already owns rather than stacking a new one
      // on every execute. Raw values are hashed at rest and never re-readable, so the
      // existing row's secret is unusable — revoking + re-issuing the SAME named row is
      // how it gains a usable value again (exactly what Rotate does). Integration
      // tokens keep their own rows untouched.
      const priors = await findTryItTokens(fetchApi, form.id);
      for (const t of priors) await revokeWorkflowToken(fetchApi, t.id);
      const raw = await issueWorkflowToken(fetchApi, {
        workflowId: form.id,
        name: `${TRY_IT_PREFIX}${form.name || form.key || form.id}`.slice(0, 100),
        days: 1,
      });
      if (!raw) throw new Error('The token service returned no value');
      setToken(raw);
      setAutoIssued(true);
      onIssued && onIssued(raw);
      return raw;
    } catch (ex) {
      setIssueError(`Could not reuse or issue the Try-it token: ${
        (ex.response && ex.response.data && ex.response.data.error) || ex.message
      } — paste a tnt_ token above instead.`);
      return null;
    } finally {
      setIssuing(false);
    }
  };

  const forget = (alsoForgetIssued) => {
    setToken('');
    setAutoIssued(false);
    setIssueError('');
    alsoForgetIssued && alsoForgetIssued();
  };

  return { token, setToken, ensureToken, autoIssued, issuing, issueError, forget };
}

/** Form/JSON switch. Disabled — with the reason as a tooltip — whenever the payload cannot
 *  be represented as fields, so the control never lies about being available. */
function ModeToggle({ mode, onMode, formBlocked }) {
  const tab = (value, Icon, label) => (
    <button
      type="button"
      onClick={() => onMode(value)}
      disabled={value === 'form' && !!formBlocked}
      title={value === 'form' && formBlocked ? formBlocked : undefined}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${
        mode === value ? 'bg-primary-600/10 text-primary-600 dark:text-primary-400' : 'text-secondary-500 hover:text-[var(--app-text)]'
      } disabled:text-secondary-400 disabled:cursor-not-allowed disabled:hover:text-secondary-400`}
    >
      <Icon size={12} /> {label}
    </button>
  );
  return (
    <div className="flex items-center gap-0.5">
      {tab('form', ListChecks, 'Form')}
      {tab('json', Braces, 'JSON')}
    </div>
  );
}

/**
 * The request payload, as a form over the declared inputs or as raw JSON.
 *
 * `bodyText` is the single source of truth in BOTH modes — the form reads `input` out of it
 * and splices `input` back (lib/runPayload.js), so everything downstream (execute, Copy
 * cURL, the idempotency-key merge) is untouched and the four LLM-App spec overrides, a
 * hand-typed idempotency_key and any unknown key all survive a round-trip.
 */
function RequestBodyEditor({
  bodyText, setBodyText, onFormat, onReset, fields, mode, onMode, formBlocked,
}) {
  const hasFields = namedFields(fields).length > 0;
  const showForm = mode === 'form' && !formBlocked && hasFields;
  const parsed = showForm ? readInput(bodyText) : null;

  const setField = (name, value) => {
    const current = parsed && parsed.ok ? parsed.input : {};
    setBodyText(writeInput(bodyText, applyValues(current, fields, { ...valuesFromInput(fields, current), [name]: value })));
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
          Request Payload {showForm ? '' : '(JSON)'}
        </label>
        <div className="flex items-center gap-2 text-xs">
          {hasFields && <ModeToggle mode={mode} onMode={onMode} formBlocked={formBlocked} />}
          {hasFields && <span className="text-secondary-400">·</span>}
          {!showForm && (
            <>
              <button
                type="button"
                onClick={onFormat}
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
                title="Beautify and validate JSON"
              >
                <AlignLeft size={12} /> Format JSON
              </button>
              <span className="text-secondary-400">·</span>
            </>
          )}
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-secondary-500 hover:text-[var(--app-text)]"
            title="Reset to default example"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      {showForm ? (
        <div className="rounded-md border border-[var(--app-border)] p-3 space-y-3">
          <InputSchemaForm
            fields={fields}
            values={valuesFromInput(fields, (parsed && parsed.ok && parsed.input) || {})}
            onChange={setField}
          />
          <p className="text-[10px] text-secondary-500">
            Edits the <code>input</code> object only — everything else in the payload is left
            exactly as it is. Switch to JSON to see or change the rest.
          </p>
        </div>
      ) : (
        <Suspense fallback={<div className="h-[180px] w-full rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] animate-pulse" />}>
          <CodeEditor
            value={bodyText}
            onChange={setBodyText}
            language="json"
            height="180px"
            placeholder="{ ... }"
          />
        </Suspense>
      )}

      {hasFields && formBlocked && mode === 'form' && (
        <p className="text-[10px] text-amber-600">{`Showing JSON — ${formBlocked}`}</p>
      )}
    </div>
  );
}

function ExecuteActionsRow({ onExecute, running, issuing, onCopyCurl, copied, polling, onStopPolling }) {
  let buttonLabel = 'Execute';
  if (issuing) buttonLabel = 'Issuing token…';
  else if (running) buttonLabel = 'Executing…';

  return (
    <div className="flex items-center gap-2 pt-1">
      <Button onClick={onExecute} disabled={running || issuing}>
        <Play size={13} className="inline mr-1" />{buttonLabel}
      </Button>
      <Button size="sm" variant="secondary" onClick={onCopyCurl}>
        {copied ? <Check size={13} className="inline mr-1 text-emerald-600" /> : <Copy size={13} className="inline mr-1" />}
        Copy cURL
      </Button>
      {polling && (
        <Button size="sm" variant="secondary" onClick={onStopPolling}>
          <Square size={13} className="inline mr-1" />Stop polling
        </Button>
      )}
    </div>
  );
}

/**
 * An UNTOUCHED payload follows later edits to the input contract; once the user has changed
 * it, it is theirs and stays frozen until Reset.
 *
 * A form keystroke counts as a touch exactly like typing in the editor does — both modes
 * write `bodyText`, so this one comparison covers them and the form needs no special case.
 */
function useExampleReseed({ bodyText, setBodyText, example, lastExample }) {
  useEffect(() => {
    if (bodyText === lastExample.current) setBodyText(example);
    lastExample.current = example;
  }, [example, bodyText, setBodyText, lastExample]);
}

/**
 * Which payload editor is showing, and whether the form is even possible.
 *
 * Form by default when the workflow declares inputs — the raw JSON textarea is the friction
 * this feature exists to remove. `formBlocked` is recomputed from the CURRENT payload on
 * every render (not latched), so pasting nested JSON falls back to the editor immediately
 * and switching back is automatic once the payload is representable again.
 */
function usePayloadMode(bodyText) {
  const [mode, setMode] = useState('form');
  const readable = readInput(bodyText);
  return {
    mode,
    setMode,
    formBlocked: readable.ok ? null : readable.reason,
    reset: () => setMode('form'),
  };
}

function ExecuteSection({ form, fields, issuedToken, onAutoIssued, onDiscardIssued }) {
  const base = `${window.location.origin}/api/v1`;
  const path = form.key || form.id;
  const runUrl = `${base}/workflows/${path}/run`;
  const example = prettyJson(buildRunBodyExample(fields, form.spec_json));

  const { fetchApi } = useAppApi();
  const tryIt = useTryItToken({ fetchApi, form, onIssued: onAutoIssued });
  const { token, setToken } = tryIt;
  const lastExample = useRef(example);
  const [bodyText, setBodyText] = useState(example);
  useExampleReseed({ bodyText, setBodyText, example, lastExample });
  const [useWait, setUseWait] = useState(true);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const payloadMode = usePayloadMode(bodyText);

  const { polling, run, steps, start: startPolling, stop: stopPolling, setRun, loadOnce } = useRunPolling({ base, token });

  useEffect(() => {
    if (issuedToken) setToken(issuedToken);
  }, [issuedToken, setToken]);

  const formatJson = () => {
    try {
      const parsed = JSON.parse(bodyText);
      setBodyText(prettyJson(parsed));
      setErr('');
    } catch {
      setErr('Request body is not valid JSON.');
    }
  };

  const execute = async () => {
    let reqBody;
    try {
      reqBody = buildRequestBody(bodyText, idempotencyKey);
    } catch {
      setErr('Request body is not valid JSON.');
      return;
    }
    const bearer = await tryIt.ensureToken();
    if (!bearer) return;
    stopPolling();
    setErr(''); setResult(null); setRun(null);
    setRunning(true);
    const started = Date.now();
    try {
      const res = await fetch(`${runUrl}${useWait ? '?wait=30' : ''}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' },
        body: reqBody,
      });
      const data = await res.json().catch(() => null);
      setResult({ status: res.status, ms: Date.now() - started, data });
      if (data) await handleRunResponse(data, { setRun, startPolling, loadOnce });
    } catch (ex) {
      setErr(ex.message || 'Request failed.');
    } finally {
      setRunning(false);
    }
  };

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(buildRunCurl({
        runUrl,
        body: curlBodyFor(bodyText, idempotencyKey),
        token: token.trim() || null,
        wait: useWait ? 30 : 0,
      }));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <Section title="Interactive Console — Try the Run Endpoint">
      <p className="text-xs text-secondary-500">
        Execute <code>POST /api/v1/workflows/{path}/run</code> live. Left blank, Execute
        reuses this workflow&apos;s own Try-it token from the list below — its 24h value is
        refreshed (old value invalidated), and no integration token is ever touched.
      </p>
      <div className="space-y-3">
        <TryItTokenBlock
          tryIt={tryIt}
          issuedToken={issuedToken}
          onForget={() => { stopPolling(); tryIt.forget(onDiscardIssued); }}
        />

        <RequestBodyEditor
          bodyText={bodyText}
          setBodyText={setBodyText}
          onFormat={formatJson}
          onReset={() => { setBodyText(example); setErr(''); payloadMode.reset(); }}
          fields={fields}
          mode={payloadMode.mode}
          onMode={payloadMode.setMode}
          formBlocked={payloadMode.formBlocked}
        />

        <RunOptionsRow
          useWait={useWait}
          onUseWait={setUseWait}
          idempotencyKey={idempotencyKey}
          onIdempotencyKey={setIdempotencyKey}
        />

        <ExecuteActionsRow
          onExecute={execute}
          running={running}
          issuing={tryIt.issuing}
          onCopyCurl={copyCurl}
          copied={copied}
          polling={polling}
          onStopPolling={stopPolling}
        />
      </div>

      {err && <Alert variant="error" title="Call not sent">{err}</Alert>}
      {tryIt.issueError && <Alert variant="warning" title="No token issued">{tryIt.issueError}</Alert>}
      <ResultView result={result} polling={polling} run={run} steps={steps} />
    </Section>
  );
}

/* ----------------------------------------------------------- code snippets card -- */

function CodeSnippetsCard({ form, fields }) {
  const base = `${window.location.origin}/api/v1`;
  const path = form.key || form.id;
  const runUrl = `${base}/workflows/${path}/run`;
  const body = prettyJson(buildRunBodyExample(fields, form.spec_json));

  const [tab, setTab] = useState('curl_async');

  const snippets = {
    curl_async: {
      label: 'cURL (Async)',
      code: buildRunCurl({ runUrl, body }),
      desc: 'Standard async trigger: returns 202 immediately with run_id.',
    },
    curl_wait: {
      label: 'cURL (Wait 30s)',
      code: buildRunCurl({ runUrl, body, wait: 30 }),
      desc: 'Long-poll trigger: blocks up to 30s before returning 202.',
    },
    js: {
      label: 'JavaScript',
      code: buildRunJsFetch({ runUrl, body, wait: 30 }),
      desc: 'Modern fetch snippet with long-polling (?wait=30).',
    },
    python: {
      label: 'Python',
      code: buildRunPythonRequests({ runUrl, body, wait: 30 }),
      desc: 'Python script using requests library with ?wait=30.',
    },
    json: {
      label: 'Payload',
      code: body,
      desc: 'JSON body matching the workflow input schema.',
    },
  };

  const activeSnippet = snippets[tab] || snippets.curl_async;

  return (
    <Card className="p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-sm">Code Examples</h3>
        <div className="flex items-center gap-1 bg-[var(--app-surface)] p-0.5 rounded-md border border-[var(--app-border)]">
          {Object.entries(snippets).map(([key, item]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                tab === key
                  ? 'bg-primary-600 text-white shadow-xs'
                  : 'text-secondary-600 hover:text-[var(--app-text)]'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-secondary-500">{activeSnippet.desc}</p>
      <CodeBlock code={activeSnippet.code} />
    </Card>
  );
}

/* ------------------------------------------------------- status lifecycle guide -- */

function StatusLifecycleGuide() {
  const steps = [
    {
      code: '202',
      badge: <Badge variant="info" size="sm">202 Queued</Badge>,
      title: 'Run Accepted',
      desc: 'Returns { run_id, status: "queued", poll: "/api/v1/runs/:id" }',
    },
    {
      code: 'running',
      badge: <Badge variant="outline" size="sm">running</Badge>,
      title: 'In Flight',
      desc: 'Execution active. Poll GET /api/v1/runs/:id until completion.',
    },
    {
      code: 'done',
      badge: <Badge variant="success" size="sm">done</Badge>,
      title: 'Finished',
      desc: 'Execution completed. Final output is available in run.output.',
    },
    {
      code: 'error',
      badge: <Badge variant="error" size="sm">error</Badge>,
      title: 'Failed',
      desc: 'Check run.error for explanation. Cancelled runs show as cancelled.',
    },
  ];

  return (
    <Card className="p-3 space-y-3">
      <h3 className="font-semibold text-sm">Response & Execution Lifecycle</h3>
      <div className="space-y-1.5">
        {steps.map((s) => (
          <div key={s.code} className="flex items-start gap-2.5 p-2 rounded-md bg-[var(--app-surface)]/60 border border-[var(--app-border)]/50 text-xs">
            <div className="shrink-0 pt-0.5">{s.badge}</div>
            <div className="min-w-0 flex-1">
              <span className="font-medium text-[var(--app-text)]">{s.title}</span>
              <p className="text-secondary-500 mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* --------------------------------------------------------- output structure card -- */

function OutputStructureCard({ graph }) {
  const shape = outputShapeFor(graph);
  return (
    <Card className="p-3 space-y-2">
      <h3 className="font-semibold text-sm">Output Structure</h3>
      {shape.mode === 'template' && (
        <p className="text-xs text-secondary-500">
          This workflow&apos;s Output node has a template, so <code>run.output.output</code> is the rendered string.
        </p>
      )}
      {shape.mode === 'scope' && (
        <p className="text-xs text-secondary-500">
          The Output node has an empty template, so results land in <code>run.output.output.&lt;node_id&gt;</code>.
        </p>
      )}
      {shape.mode === 'none' && (
        <Alert variant="warning" title="No Output node">
          This graph has no Output node — add one in Build tab so runs can produce a result.
        </Alert>
      )}
      {shape.mode !== 'none' && (
        <CodeBlock code={shape.mode === 'template'
          ? prettyJson({ run: { id: 42, status: 'done', output: { output: `…rendered: ${shape.template}` } } })
          : prettyJson({ run: { id: 42, status: 'done', output: { output: { '<node_id>': { /* node output */ } } } } })} />
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------------------- main -- */

export default function ApiPanel({ form, fields, graph }) {
  const [issuedToken, setIssuedToken] = useState(null);
  // Bumped whenever Execute reuses/refreshes the Try-it token — TokensSection reloads so
  // the refreshed row (new prefix, updated last-used) shows immediately.
  const [tokenActivity, setTokenActivity] = useState(0);

  return (
    <div className="space-y-4 w-full">
      <EndpointHeroCard form={form} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-7 space-y-4">
          <ExecuteSection
            form={form}
            fields={fields}
            issuedToken={issuedToken}
            onAutoIssued={() => setTokenActivity((n) => n + 1)}
            onDiscardIssued={() => setIssuedToken(null)}
          />
        </div>

        <div className="lg:col-span-5 space-y-4">
          <CodeSnippetsCard form={form} fields={fields} />
          <StatusLifecycleGuide />
          <OutputStructureCard graph={graph} />
        </div>
      </div>

      <div className="space-y-4 pt-2">
        <TokensSection workflowId={form.id} onIssued={setIssuedToken} reloadKey={tokenActivity} />
        <UsageSection workflowId={form.id} />
      </div>
    </div>
  );
}
